import { Card, CardHeader, CodeChip, KeyValue, Table, Tbody, Td, Th, Thead, Tr } from '@provenance/design';
import { auDate, countryWithCode, money, quantity } from '../../lib/format';

/**
 * The submitted data, rendered beside the document.
 *
 * This is one of the two sources of truth the operator is comparing (spec §8.1): what
 * the exporter *declared* against what the certificate *says*. Laying it out plainly
 * next to the PDF is most of the value of the review screen — the completeness check
 * then only has to point at the places they disagree.
 *
 * `structuredData` is the verbatim §9.2 payload, held as JSONB. It is read defensively
 * rather than cast, because an integrator can send a shape we have not seen: a missing
 * consignee should render as "not supplied", not crash the screen an operator needs.
 */

interface GoodsLine {
  lineNumber?: number;
  hsCode?: string;
  description?: string;
  marksAndNumbers?: string | null;
  packageCount?: number | null;
  packageKind?: string | null;
  quantity?: string;
  unit?: string;
  grossWeight?: string | null;
  netWeight?: string | null;
  value?: string | null;
  currency?: string;
  countryOfOrigin?: string;
  originCriterion?: string | null;
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
}

interface Party {
  companyName?: string;
  abn?: string | null;
  address?: {
    line1?: string;
    line2?: string | null;
    suburb?: string | null;
    state?: string | null;
    postcode?: string | null;
    country?: string;
  };
  contactName?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
}

interface Shipment {
  finalDestinationCountry?: string;
  portOfLoading?: string | null;
  portOfDischarge?: string | null;
  vesselOrFlight?: string | null;
  departureDate?: string | null;
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
  invoiceTotal?: string | null;
  currency?: string;
  remarks?: string | null;
  goodsLines?: GoodsLine[];
}

export function ShipmentPanel({ data }: { data: Record<string, unknown> }) {
  const exporter = (data['exporter'] ?? {}) as Party;
  const consignee = (data['consignee'] ?? null) as Party | null;
  const producer = (data['producer'] ?? null) as Party | null;
  const shipment = (data['shipment'] ?? {}) as Shipment;
  const lines = shipment.goodsLines ?? [];
  const currency = shipment.currency ?? 'AUD';

  const lineTotal = lines.reduce((sum, line) => sum + Number(line.value ?? 0), 0);
  const netTotal = lines.reduce((sum, line) => sum + Number(line.netWeight ?? 0), 0);

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title="Parties"
          hint="As submitted"
          description="What the exporter declared. Compare against the certificate."
        />
        <div className="grid gap-5 md:grid-cols-2">
          <PartyBlock label="Exporter" party={exporter} />
          {consignee ? (
            <PartyBlock label="Consignee" party={consignee} />
          ) : (
            <div>
              <p className="pv-eyebrow text-[var(--pv-faint)]">Consignee</p>
              <p className="mt-1.5 [font-size:13px] text-[var(--pv-faint)]">Not supplied.</p>
            </div>
          )}
          {producer && <PartyBlock label="Producer" party={producer} />}
        </div>
      </Card>

      <Card>
        <CardHeader title="Shipment" hint="As submitted" />
        <dl className="m-0 grid gap-x-6 md:grid-cols-2">
          <KeyValue label="Destination">{countryWithCode(shipment.finalDestinationCountry)}</KeyValue>
          <KeyValue label="Departure">{auDate(shipment.departureDate)}</KeyValue>
          <KeyValue label="Port of loading">{shipment.portOfLoading || '—'}</KeyValue>
          <KeyValue label="Port of discharge">{shipment.portOfDischarge || '—'}</KeyValue>
          <KeyValue label="Vessel / flight">{shipment.vesselOrFlight || '—'}</KeyValue>
          <KeyValue label="Invoice" mono>
            {shipment.invoiceNumber || '—'}
          </KeyValue>
          <KeyValue label="Invoice date">{auDate(shipment.invoiceDate)}</KeyValue>
          <KeyValue label="Invoice total">{money(shipment.invoiceTotal, currency)}</KeyValue>
        </dl>
        {shipment.remarks && (
          <p className="mt-4 border-l-2 border-[var(--pv-leaf)] pl-3 [font-size:13px] leading-relaxed text-[var(--pv-ink-soft)]">
            {shipment.remarks}
          </p>
        )}
      </Card>

      <Card padded={false}>
        <div className="px-5 pt-5">
          <CardHeader
            title="Goods"
            hint={`${lines.length} line${lines.length === 1 ? '' : 's'}`}
            description="Each line becomes a row on the certificate."
          />
        </div>
        <div className="pv-scroll-x border-t border-[var(--pv-line)]">
          <Table className="min-w-[760px]">
            <Thead>
              <Tr>
                <Th className="w-10">#</Th>
                <Th>Description</Th>
                <Th>HS code</Th>
                <Th>Origin</Th>
                <Th numeric>Quantity</Th>
                <Th numeric>Net kg</Th>
                <Th numeric>Value</Th>
              </Tr>
            </Thead>
            <Tbody>
              {lines.map((line, index) => (
                <Tr key={`${line.hsCode}-${index}`}>
                  <Td numeric>{line.lineNumber ?? index + 1}</Td>
                  <Td>
                    <span className="font-medium">{line.description || '—'}</span>
                    {(line.packageCount || line.marksAndNumbers) && (
                      <span className="mt-0.5 block [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
                        {line.packageCount
                          ? `${line.packageCount} ${line.packageKind ?? 'packages'}`
                          : null}
                        {line.packageCount && line.marksAndNumbers ? ' · ' : null}
                        {line.marksAndNumbers}
                      </span>
                    )}
                  </Td>
                  <Td>
                    <CodeChip>{line.hsCode || '—'}</CodeChip>
                  </Td>
                  <Td>
                    {line.countryOfOrigin ?? '—'}
                    {line.originCriterion && (
                      <span className="mt-0.5 block [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
                        {line.originCriterion}
                      </span>
                    )}
                  </Td>
                  <Td numeric>{quantity(line.quantity, line.unit)}</Td>
                  <Td numeric>{line.netWeight ? quantity(line.netWeight) : '—'}</Td>
                  <Td numeric>{money(line.value, line.currency ?? currency)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </div>
        <div className="flex flex-wrap items-center gap-4 border-t border-[var(--pv-line)] bg-[var(--pv-paper-sunk)] px-5 py-3 [font-size:var(--pv-text-xs)]">
          <span className="text-[var(--pv-muted)]">
            Lines total{' '}
            <strong className="pv-num font-semibold text-[var(--pv-ink)]">
              {money(lineTotal, currency)}
            </strong>
          </span>
          {netTotal > 0 && (
            <span className="text-[var(--pv-muted)]">
              Net weight{' '}
              <strong className="pv-num font-semibold text-[var(--pv-ink)]">
                {quantity(netTotal, 'kg')}
              </strong>
            </span>
          )}
          {/* The arithmetic the completeness check performs, shown inline so an operator
              can see the discrepancy without cross-referencing the panel. */}
          {shipment.invoiceTotal && Math.abs(lineTotal - Number(shipment.invoiceTotal)) > 0.01 && (
            <span className="font-semibold text-[var(--pv-stop)]">
              Declared invoice total is {money(shipment.invoiceTotal, currency)} — a difference of{' '}
              {money(Math.abs(lineTotal - Number(shipment.invoiceTotal)), currency)}
            </span>
          )}
        </div>
      </Card>
    </div>
  );
}

function PartyBlock({ label, party }: { label: string; party: Party }) {
  const address = party.address ?? {};
  const lines = [
    address.line1,
    address.line2,
    [address.suburb, address.state, address.postcode].filter(Boolean).join(' '),
    address.country ? countryWithCode(address.country) : null,
  ].filter((line): line is string => Boolean(line && line.trim()));

  return (
    <div className="min-w-0">
      <p className="pv-eyebrow text-[var(--pv-faint)]">{label}</p>
      <p className="mt-1.5 [font-size:13.5px] font-semibold">{party.companyName || '—'}</p>
      {party.abn && (
        <p className="pv-mono mt-0.5 [font-size:var(--pv-text-2xs)] text-[var(--pv-muted)]">
          ABN {party.abn}
        </p>
      )}
      {lines.length > 0 && (
        <address className="mt-1.5 [font-size:13px] not-italic leading-relaxed text-[var(--pv-ink-soft)]">
          {lines.map((line) => (
            <span key={line} className="block">
              {line}
            </span>
          ))}
        </address>
      )}
      {(party.contactName || party.contactEmail || party.contactPhone) && (
        <p className="mt-1.5 [font-size:var(--pv-text-xs)] text-[var(--pv-muted)]">
          {[party.contactName, party.contactEmail, party.contactPhone].filter(Boolean).join(' · ')}
        </p>
      )}
    </div>
  );
}
